// import { AddDatasetEntry } from "./AddDatasetEntry";
import {Geometry} from "geojson";
import {Dataset} from "./data/Dataset";
import {ColorPalette, MapOfColorMaps} from "./util/Color";
import {DATASET_DEFAULTS} from "./datasetDefaultsDef";

// import { IconType } from "./icons/iconDefinitions";

export interface Api extends SelectionListener {
    initialize(bootstrapParameters: BootstrapParameters | string): void;

    setState(state: object | string): void;

    getState(asJson?: boolean): string | object;

    setGlobalSettings(settings: any): void;

    addDataset(dataset: DatasetDef, data: RowCell[][]): void;

    // addDatasets(entries: AddDatasetEntry[]): void;

    refreshDataset(datasetId: DatasetDefId, data: RowCell[][], centerMap?: boolean): void;

    setTheme(sysTheme: ThemeType): void;

    getVersion(): string;

    getCommitHash(): string;

    getBuildTimestamp(): number | undefined;

    addSelectionListener(listener: SelectionListener): void;

    removeSelectionListener(listener: SelectionListener): void;
}

export type DatasetDefaults = {
    displayName: string;
    layerType?: LayerType;
    geoFieldType?: GeoFieldType;
    colors?: string[];
    numOfColorsToGenerate?: number;
    pointSize?: number; // value 0 - 100
    opacity?: number; // value 0 - 100
    shape?: string;
    field?: FieldDefId;
    sizeField?: FieldDefId;
    tooltipFields?: FieldDefId[];
    height?: number;
    heightField?: FieldDefId;
    directionField?: FieldDefId;
};

export type MapOfDatasetDefaults = { [key: string]: DatasetDefaults };

export class BootstrapParameters {
    theme?: "light" | "dark" = "light";
    standalone?: boolean = false;
    devInit?: boolean = false;
    isDebugUser?: boolean = false;
    defaults?: MapOfDatasetDefaults = DATASET_DEFAULTS;
    username?: string = null;
    globalUsername?: string = null;
    consoleHandler?: string;

    update = (data: Partial<BootstrapParameters>): void => {
        Object.assign(this, data);
    };

    getDatasetDefaults = (datasetType: string): DatasetDefaults => {
        return this?.defaults?.[datasetType?.toLowerCase()];
    }
}

export interface SelectionListener<T = any> {
    onSelect(selection: T): void;
}

export enum MapStyle {
    standard = "standard",
    terrain = "terrain",
    satellite = "satellite",
}

export enum ThemeType {
    light = "light",
    dark = "dark"
}

export enum AppThemeType {
    light = "light",
    dark = "dark",
    system = "system",
}

/**
 * The savable widget configuration
 */
export type CMAPState = {
    version: "1.0";

    theme: AppThemeType;

    mapType: "normal" | "terrain" | "satellite";

    viewType: "2D" | "3D";

    /** not supported yet, todo*/
    activePanel?: "layers" | "data" | "settings";
    /** List of saved datasets */
    datasets: DatasetDef[];
    /** List of saved Layers */
    layers: Object[];

    showLegend?: boolean;
};

export class LayerDef {
    constructor(data: Partial<LayerDef> = {}) {
        Object.assign(this, data);
    }

    readonly id: string;
    name: string;
    type: LayerType;
    dataset: Dataset;

    headingFieldId?: FieldDefId;

    geoFieldType?: GeoFieldType;
    isHidden?: boolean = false;

    config: Partial<LayerPointConfig>;

    isColorFieldNumeric = (): boolean => {
        const colorField = this.dataset?.getFieldDefById(this.config.colorFieldId);
        const type = colorField?.type;
        return type === "real" || type === "integer";
    }
}

export type LayerConfig<T extends LayerType> = T extends "point" ? LayerPointConfig : never;



export type LayerType = "point" | "cluster" | "geojson";


export type LayerPointConfig = {
    colorFieldId: string;
    colorPalette: ColorPalette;
    useGradient: boolean;
    customPaletteIndex?: number;
    colorsMapsByFields: MapOfColorMaps;
    hiddenColors?: Set<any>;

    opacity: number;
    size: number;
    sizeDependsOnSizeField: boolean;
    sizeFieldId: FieldDefId;
    tooltipFields?: FieldDefId[];
    height: number;

    heightFieldId: FieldDefId;
    useHeightField: boolean;

    /** Use heading field (if set) to point icons to heading field value */
    shape: string;
    useHeadingField: boolean;
};
export type DatasetDefId = string;
export type FieldDefId = string;

export type DatasetDef = {
    /**
     * Unique dataset id
     */
    id: DatasetDefId;

    fields: FieldDef[];
    info?: {
        symbols?: string[];
        /** Dataset classification type */
        type?: string;
        /** Proposed dataset name */
        name?: string;
        /** Default value field ID*/
        valueField?: string;
        /** Default name field ID */
        nameField?: string;
        /** Default latitude field ID */
        latField?: string;
        /** Default longitude field ID */
        lonField?: string;
        /** Default geoJSON field ID */
        geoField?: string;
        /** Description field */
        descField?: string;
        /** Identifier (Primary Key) field */
        identField?: string;
        countryField?: string;
        stateField?: string;
        countyField?: string;
        headingField?: string;
    };
};

export type FieldDef = {
    id: FieldDefId;
    name: string;
    type: FieldType;
};
export type FieldType = "integer" | "real" | "string" | "geojson";

export type RowCell = number | string | null | undefined | boolean | Geometry;

export type GeoFieldType = "lat&lng" | "geometry" | "country" | "state" | "county";
